import React, { useState } from "react";
import { Input } from "antd";
import { PriceInput } from "../input-number";
import { ChosenPriceDisabledTooltip } from "./chosen-price-disabled-tooltip";
import { PriceRange } from "../../data-types";
import styles from "./sidebar-price-input-range.module.css";
import { useDebouncedCallback } from "use-debounce";

const { number: numberClassName, hyphen: hyphenClassName } = styles;
type RangeProps = {
  chosen: PriceRange;
  onChange: (arg0: { lower: number; upper: number }) => void;
  disabled: boolean;
  currencySymbol: string;
  phaseLevelPricing: boolean;
  topDownPricing: boolean;
};

const SidebarPriceInputRangeDisabled: React.FC<
  Pick<RangeProps, "chosen" | "currencySymbol" | "phaseLevelPricing" | "topDownPricing">
> = ({ chosen, currencySymbol, phaseLevelPricing, topDownPricing }) => (
  <ChosenPriceDisabledTooltip phaseLevelPricing={phaseLevelPricing} topDownPricing={topDownPricing}>
    <PriceInput
      currencySymbol={currencySymbol}
      size="small"
      className={numberClassName}
      placeholder="Low"
      value={chosen?.lower ?? undefined}
      step={100}
      disabled
    />
    <Input size="small" className={hyphenClassName} placeholder="-" disabled />
    <PriceInput
      currencySymbol={currencySymbol}
      size="small"
      className={numberClassName}
      placeholder="High"
      value={chosen?.upper ?? undefined}
      step={100}
      disabled
    />
  </ChosenPriceDisabledTooltip>
);
const SidebarPriceInputRangeEnabled: React.FC<Omit<RangeProps, "disabled">> = ({
  chosen,
  onChange,
  currencySymbol,
}) => {
  const [priceRange, setPriceRange] = useState<{
    lower: number;
    upper: number;
  }>({
    lower: chosen.lower || 0,
    upper: chosen.upper || 0,
  });

  const debounceOnChange = useDebouncedCallback((value: number, type: "lower" | "upper") => {
    const isLower = type === "lower";
    const valid = isLower ? value <= priceRange.upper : value >= priceRange.lower;

    const newValue = valid ? { ...priceRange, [type]: value } : { lower: value, upper: value };

    setPriceRange(newValue);

    onChange(newValue);
  }, 600);

  return (
    <>
      <PriceInput
        currencySymbol={currencySymbol}
        size="small"
        className={numberClassName}
        placeholder="Low"
        onChange={(value) => debounceOnChange(value, "lower")}
        step={100}
        value={priceRange.lower ?? undefined}
      />
      <Input size="small" className={hyphenClassName} placeholder="-" disabled />
      <PriceInput
        currencySymbol={currencySymbol}
        size="small"
        className={numberClassName}
        placeholder="High"
        onChange={(value) => debounceOnChange(value, "upper")}
        value={priceRange.upper ?? undefined}
        step={100}
      />
    </>
  );
};
export const SidebarPriceInputRange: React.FC<RangeProps> = ({ disabled, ...others }) => {
  return disabled ? (
    <SidebarPriceInputRangeDisabled {...others} />
  ) : (
    <SidebarPriceInputRangeEnabled {...others} />
  );
};
